import React, { useEffect, useRef, useState } from "react";
import { Button, Spinner } from "reactstrap";

const BRAND_BLUE = "#0147C8";

const CategoryInfiniteScroll = ({
    currentCount = 0,
    totalCount = 0,
    hasMore = false,
    isLoading = false,
    isLoadingMore = false,
    onLoadMore,
    categoryName = "",
}) => {
    const observerTarget = useRef(null);
    const [showScrollTop, setShowScrollTop] = useState(false);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        if (!hasMore || isLoadingMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
                    onLoadMore();
                }
            },
            { threshold: 0.1 }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [hasMore, isLoadingMore, onLoadMore]);

    // Show/hide scroll to top button based on scroll position
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            setShowScrollTop(scrollTop > 300);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Scroll to top function
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <>
            {/* Progress indicator */}
            {currentCount > 0 && (
                <div className="mb-3 d-flex justify-content-end align-items-center">
                    <p className="text-muted mb-0">
                        <strong>
                            Displaying {currentCount} out of {totalCount}
                        </strong>{" "}
                        {categoryName} assets
                    </p>
                </div>
            )}

            {/* Load More Section */}
            {hasMore && (
                <div ref={observerTarget} className="text-center py-4">
                    {isLoadingMore ? (
                        <div className="d-flex align-items-center justify-content-center">
                            <Spinner
                                style={{
                                    width: "2rem",
                                    height: "2rem",
                                    color: BRAND_BLUE,
                                    marginRight: "10px",
                                }}
                            />
                            <span className="text-muted">Loading more...</span>
                        </div>
                    ) : (
                        <Button
                            onClick={onLoadMore}
                            style={{
                                backgroundColor: BRAND_BLUE,
                                borderColor: BRAND_BLUE,
                                color: "white",
                            }}
                        >
                            Load More ({totalCount - currentCount} remaining)
                        </Button>
                    )}
                </div>
            )}

            {/* Completed message */}
            {!hasMore && currentCount > 0 && (
                <div className="text-center py-3">
                    <p className="text-muted">
                        Showing all {currentCount} of {totalCount} assets
                    </p>
                </div>
            )}

            {/* Floating Scroll to Top Button */}
            {showScrollTop && (
                <Button
                    onClick={scrollToTop}
                    style={{
                        position: "fixed",
                        bottom: "30px",
                        right: "30px",
                        width: "50px",
                        height: "50px",
                        borderRadius: "50%",
                        backgroundColor: BRAND_BLUE,
                        borderColor: BRAND_BLUE,
                        color: "white",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                        zIndex: 1000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.1)";
                        e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
                    }}
                >
                    <i className="ri-arrow-up-line" style={{ fontSize: "24px" }}></i>
                </Button>
            )}
        </>
    );
};

export default CategoryInfiniteScroll;
